import React, { useRef, useEffect } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { Card, Text, Icon, Button, Portal, Modal, IconButton } from 'react-native-paper';
import { spacing } from '../constants/theme';
import { reviewService } from '../services/reviewService';
import { api } from '../services/api';
import { currentEnvironment } from '../config/environments';
import { MAX_PHOTOS_PER_UPLOAD } from '../constants/photoLimits';

interface PhotoCaptureProps {
  reviewId: number;
  responseId: number;
  responseTempId?: string;
  itemId: number;
  initialPhotos?: any[];
  onPhotosChange: (photos: any[]) => void;
  maxPhotos?: number;
  questionId?: string;
  questionLabel?: string;
}

export const PhotoCapture: React.FC<PhotoCaptureProps> = ({
  reviewId,
  responseId,
  responseTempId,
  itemId,
  initialPhotos = [],
  onPhotosChange,
  maxPhotos = MAX_PHOTOS_PER_UPLOAD,
  questionId,
  questionLabel,
}) => {
  const WebView = View as any;
  const PaperIcon = Icon as any;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [photos, setPhotos] = React.useState(initialPhotos);
  const [uploading, setUploading] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [deletingPhotoIds, setDeletingPhotoIds] = React.useState<Set<string>>(new Set());
  const [viewerVisible, setViewerVisible] = React.useState(false);
  const [viewerIndex, setViewerIndex] = React.useState(0);
  const [editingNote, setEditingNote] = React.useState(false);
  const [noteText, setNoteText] = React.useState('');
  const [savingNote, setSavingNote] = React.useState(false);


  // Load existing photos when component mounts
  React.useEffect(() => {
    const loadExistingPhotos = async () => {
      try {
        const existingPhotos = await reviewService.getPhotosForQuestion(
          reviewId,
          responseId,
          itemId,
          questionId
        );
        
        if (existingPhotos.length > 0) {
          // For web, use presigned URLs from review data
          const formattedPhotos = existingPhotos.map((photo: any) => {
            const imageUri = photo.presignedUrl || photo.s3Url || photo.url;

            return {
              id: `backend_${photo.id}`,
              uri: imageUri,
              fileName: photo.fileName,
              type: photo.mimeType,
              size: photo.size,
              uploaded: true,
              uploading: false,
              backendId: photo.id,
              note: photo.note || '',
            };
          });
          
          setPhotos(formattedPhotos);
        }
      } catch (error) {
        console.error('Failed to load existing photos:', error);
      } finally {
        setLoading(false);
      }
    };

    loadExistingPhotos();
  }, [reviewId, responseId, itemId, questionId]);

  // Notify parent of photo changes after state updates settle
  React.useEffect(() => {
    if (!loading) {
      // Use setTimeout to ensure this runs after the current render cycle
      const timeoutId = setTimeout(() => {
        onPhotosChange(photos);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [photos, loading, onPhotosChange]);

  // Keyboard navigation for viewer
  useEffect(() => {
    if (!viewerVisible || photos.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && viewerIndex > 0) {
        setViewerIndex(viewerIndex - 1);
      } else if (e.key === 'ArrowRight' && viewerIndex < photos.length - 1) {
        setViewerIndex(viewerIndex + 1);
      } else if (e.key === 'Escape') {
        setViewerVisible(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewerVisible, viewerIndex, photos.length]);

  // Update note text when viewer index changes
  useEffect(() => {
    if (viewerVisible && photos[viewerIndex]) {
      setNoteText(photos[viewerIndex].note || '');
      setEditingNote(false);
    }
  }, [viewerIndex, viewerVisible, photos]);

  // Focus textarea when editing mode is enabled
  useEffect(() => {
    if (editingNote && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [editingNote]);

  const handleFileSelect = async (event: any) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newPhotos = [];
    for (let i = 0; i < files.length && photos.length + newPhotos.length < maxPhotos; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;

      const photo = {
        id: `web_${Date.now()}_${i}`,
        uri: URL.createObjectURL(file),
        fileName: file.name,
        type: file.type,
        size: file.size,
        file: file, // Store the actual file for upload
        uploaded: false,
        uploading: false,
      };
      newPhotos.push(photo);
    }

    if (newPhotos.length > 0) {
      const updatedPhotos = [...photos, ...newPhotos];
      setPhotos(updatedPhotos);
      
      // Upload photos to backend
      uploadPhotosToBackend(newPhotos, updatedPhotos);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = async (photoId: string) => {
    const photo = photos.find(p => p.id === photoId);
    if (!photo) return;

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete "${photo.fileName}"?\n\nThis action cannot be undone and the photo will be permanently removed.`
    );

    if (!confirmed) return;

    try {
      // Mark photo as deleting
      setDeletingPhotoIds(prev => new Set(prev).add(photoId));

      // If it's a backend photo (has backendId), delete from server
      if (photo.backendId) {
        await reviewService.deletePhoto(photo.backendId);
        reviewService.invalidateResponsePhotoCache(responseId);
      }

      // Remove from local state
      const updatedPhotos = photos.filter(p => p.id !== photoId);
      setPhotos(updatedPhotos);
    } catch (error) {
      console.error('Failed to delete photo:', error);
      alert('Failed to delete photo. Please try again.');
    } finally {
      // Remove deleting state
      setDeletingPhotoIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(photoId);
        return newSet;
      });
    }
  };

  const saveNote = async () => {
    const photo = photos[viewerIndex];

    if (!photo) return;

    if (!photo.backendId) {
      if (photo.uploading) {
        alert('Photo is still uploading. Please wait for the upload to complete before adding a note.');
      } else if (photo.uploaded === false) {
        alert('Photo upload failed. Please remove and try uploading again.');
      } else {
        alert('Unable to save note. Please wait for the photo to finish uploading.');
      }
      return;
    }

    try {
      setSavingNote(true);
      await reviewService.updatePhotoNote(photo.backendId, noteText);

      // Update local state
      const updatedPhotos = photos.map(p =>
        p.id === photo.id ? { ...p, note: noteText } : p
      );
      setPhotos(updatedPhotos);
      setEditingNote(false);
    } catch (error) {
      console.error('Failed to save note:', error);
      alert('Failed to save note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  const cancelEditNote = () => {
    const photo = photos[viewerIndex];
    setNoteText(photo?.note || '');
    setEditingNote(false);
  };

  const uploadPhotosToBackend = async (newPhotos: any[], allPhotos: any[]) => {
    try {
      // Mark photos as uploading
      const uploadingPhotos = allPhotos.map(p =>
        newPhotos.find(np => np.id === p.id) ? { ...p, uploading: true } : p
      );
      setPhotos(uploadingPhotos);

      // Prepare files for backend
      const filesToUpload = newPhotos.map(photo => ({
        file: photo.file,
        uri: photo.uri,
        name: photo.fileName,
        type: photo.type
      }));

      // Upload to backend and capture response
      const uploadedPhotosData = await reviewService.uploadPhotos(reviewId, responseId, itemId, filesToUpload, questionId);

      // Mark as uploaded and merge backendId from response
      const uploadedPhotos = uploadingPhotos.map(p => {
        const matchingNewPhoto = newPhotos.find(np => np.id === p.id);
        if (matchingNewPhoto) {
          // Find corresponding backend photo by matching filename
          const backendPhoto = uploadedPhotosData.find((bp: any) =>
            bp.fileName === matchingNewPhoto.fileName
          );
          return {
            ...p,
            uploading: false,
            uploaded: true,
            backendId: backendPhoto?.id,
          };
        }
        return p;
      });
      setPhotos(uploadedPhotos);
      reviewService.invalidateResponsePhotoCache(responseId);
    } catch (error) {
      console.error('Upload failed:', error);
      // Mark as failed
      const failedPhotos = allPhotos.map(p =>
        newPhotos.find(np => np.id === p.id) ? { ...p, uploading: false, uploaded: false } : p
      );
      setPhotos(failedPhotos);
    }
  };

  const canAddMore = photos.length < maxPhotos;

  if (loading) {
    return (
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.container}>
            <View style={styles.header}>
              <Icon source="camera-off" size={24} color="#666" />
              <Text style={styles.title}>
                {questionLabel ? `${questionLabel}` : 'Photos'} (Loading...)
              </Text>
            </View>
            <Text style={styles.message}>
              📷 Loading existing photos...
            </Text>
          </View>
        </Card.Content>
      </Card>
    );
  }

  return (
    <>
      <Card style={styles.card}>
      <Card.Content>
        <View style={styles.container}>
          <View style={styles.header}>
            <Icon source="camera-off" size={24} color="#666" />
            <Text style={styles.title}>
              {questionLabel ? `${questionLabel}` : 'Photos'} ({photos.length}/{maxPhotos})
            </Text>
          </View>
          
          <Text style={styles.message}>
            📷 Camera capture not available on web
          </Text>
          
          {canAddMore && (
            <View style={styles.uploadSection}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple={maxPhotos > 1}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Button
                mode="contained"
                onPress={() => fileInputRef.current?.click()}
                icon="upload"
                style={styles.uploadButton}
              >
                Upload {maxPhotos > 1 ? 'Images' : 'Image'}
              </Button>
            </View>
          )}

          {photos.length > 0 && (
            <View style={styles.photoGrid}>
              {photos.map((photo, index) => {
                const isDeleting = deletingPhotoIds.has(photo.id);
                return (
                  <View key={photo.id} style={[styles.photoItem, isDeleting && styles.photoDeleting]}>
                    {photo.uri ? (
                      <div
                        onClick={() => {
                          setViewerIndex(index);
                          setViewerVisible(true);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <img
                          src={photo.uri}
                          alt={photo.fileName}
                          style={{
                            ...styles.photoImage,
                            ...(isDeleting ? styles.imageDeleting : {}),
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <View style={styles.photoPlaceholder}>
                        <Icon source="image-off" size={48} color="#ccc" />
                        <Text style={styles.placeholderText}>Loading...</Text>
                      </View>
                    )}
                    <View style={styles.photoOverlay} pointerEvents="box-none">
                      {/* Top-right corner actions */}
                      <View style={styles.cornerActions} pointerEvents="auto">
                        <IconButton
                          icon={photo.note && photo.note.trim() !== '' ? "note-edit" : "note-plus"}
                          size={20}
                          iconColor="#fff"
                          onPress={(e: any) => {
                            e.stopPropagation();
                            setViewerIndex(index);
                            setViewerVisible(true);
                            // Small delay to ensure modal is open before setting edit mode
                            setTimeout(() => setEditingNote(true), 100);
                          }}
                          style={styles.cornerButton}
                          disabled={isDeleting}
                        />
                        <IconButton
                          icon="download"
                          size={20}
                          iconColor="#fff"
                          onPress={(e: any) => {
                            e.stopPropagation();
                            const a = document.createElement('a');
                            a.href = photo.uri;
                            a.download = photo.fileName;
                            a.target = '_blank';
                            a.rel = 'noopener noreferrer';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}
                          style={styles.cornerButton}
                          disabled={isDeleting}
                        />
                        <IconButton
                          icon={isDeleting ? "loading" : "close"}
                          size={20}
                          iconColor="#fff"
                          onPress={(e: any) => {
                            e.stopPropagation();
                            removePhoto(photo.id);
                          }}
                          style={styles.cornerButton}
                          disabled={isDeleting}
                        />
                      </View>

                      {/* Bottom filename bar */}
                      <View style={styles.filenameBar} pointerEvents="none">
                        <Text style={styles.photoName} numberOfLines={1}>
                          {photo.fileName}
                        </Text>
                      </View>
                    </View>
                    {photo.uploaded && !isDeleting && (
                      <View style={styles.uploadedBadge}>
                        <Icon source="check-circle" size={16} color="green" />
                      </View>
                    )}
                    {photo.note && photo.note.trim() !== '' && !isDeleting && (
                      <View style={styles.noteBadge}>
                        <Icon source="note-text" size={16} color="#fff" />
                      </View>
                    )}
                    {isDeleting && (
                      <View style={styles.deletingOverlay}>
                        <Text style={styles.deletingText}>Deleting...</Text>
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          )}
        </View>
      </Card.Content>
    </Card>

      {/* Photo viewer/slideshow modal */}
      <Portal>
        <Modal
          visible={viewerVisible}
          onDismiss={() => setViewerVisible(false)}
          contentContainerStyle={styles.viewerModal}
        >
          {photos.length > 0 && photos[viewerIndex] ? (
            <View style={styles.viewerContainer}>
              {/* Close button */}
              <IconButton
                icon="close"
                size={32}
                iconColor="#fff"
                onPress={() => setViewerVisible(false)}
                style={styles.viewerCloseButton}
              />

              {/* Image Area */}
              <View style={styles.imageArea}>
                {/* Current photo */}
                <img
                  src={photos[viewerIndex].uri}
                  alt={photos[viewerIndex].fileName}
                  style={styles.viewerImage as any}
                />

                {/* Previous button */}
                {viewerIndex > 0 && (
                  <IconButton
                    icon="chevron-left"
                    size={48}
                    iconColor="#fff"
                    onPress={() => setViewerIndex(viewerIndex - 1)}
                    style={styles.viewerNavLeft}
                  />
                )}

                {/* Next button */}
                {viewerIndex < photos.length - 1 && (
                  <IconButton
                    icon="chevron-right"
                    size={48}
                    iconColor="#fff"
                    onPress={() => setViewerIndex(viewerIndex + 1)}
                    style={styles.viewerNavRight}
                  />
                )}
              </View>

              {/* Controls Area - below image */}
              <View style={styles.controlsArea}>
                {/* Photo info */}
                <View style={styles.photoInfo}>
                  <Text style={styles.photoInfoText}>
                    Photo {viewerIndex + 1} / {photos.length} • {photos[viewerIndex].fileName}
                  </Text>
                </View>

                {/* Note panel */}
                <View style={styles.notePanel}>
                  {!editingNote ? (
                    <WebView
                      style={styles.noteViewContainer}
                      onClick={() => photos[viewerIndex]?.backendId && setEditingNote(true)}
                    >
                      <PaperIcon source="note-text" size={20} color="#333" style={styles.noteIcon} />
                      <Text style={styles.noteText}>
                        {noteText || 'Add a note...'}
                      </Text>
                      {photos[viewerIndex]?.backendId && (
                        <IconButton
                          icon="pencil"
                          size={16}
                          iconColor="#333"
                          onPress={() => setEditingNote(true)}
                          style={styles.noteEditButton}
                        />
                      )}
                    </WebView>
                  ) : (
                    <View style={styles.noteEditContainer}>
                      <textarea
                        ref={textareaRef}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Add a note..."
                        style={styles.noteInput as any}
                        autoFocus
                        maxLength={500}
                      />
                      <View style={styles.noteActions}>
                        <Button
                          mode="text"
                          onPress={cancelEditNote}
                          disabled={savingNote}
                        >
                          Cancel
                        </Button>
                        <Button
                          mode="contained"
                          onPress={saveNote}
                          loading={savingNote}
                          disabled={savingNote}
                        >
                          Save
                        </Button>
                      </View>
                    </View>
                  )}
                </View>

                {/* Download button */}
                <View style={styles.downloadButtonContainer}>
                  <Button
                    mode="contained"
                    icon="download"
                    onPress={() => {
                      const photo = photos[viewerIndex];
                      if (!photo) return;
                      const a = document.createElement('a');
                      a.href = photo.uri;
                      a.download = photo.fileName;
                      a.target = '_blank';
                      a.rel = 'noopener noreferrer';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }}
                  >
                    Download
                  </Button>
                </View>
              </View>
            </View>
          ) : null}
        </Modal>
      </Portal>
    </>
  );
};

const styles: any = {
  card: {
    margin: spacing.sm,
    elevation: 2,
  },
  container: {
    padding: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: spacing.sm,
    color: '#333',
  },
  message: {
    fontSize: 14,
    color: '#666',
    marginBottom: spacing.md,
  },
  uploadSection: {
    marginBottom: spacing.md,
  },
  uploadButton: {
    alignSelf: 'flex-start',
  },
  photoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,  // Increased from sm (8px) to md (16px)
  },
  photoItem: {
    position: 'relative',
    width: 200,   // Increased from 120
    height: 200,  // Increased from 120
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
  },
  photoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  photoPlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  placeholderText: {
    marginTop: spacing.xs,
    fontSize: 12,
    color: '#999',
  },
  photoOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'box-none' as any,  // Let clicks fall through to image
  },
  cornerActions: {
    position: 'absolute',
    top: 6,
    right: 6,
    flexDirection: 'row',
    gap: 4,
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: 6,
    padding: 2,
  },
  cornerButton: {
    margin: 0,
    backgroundColor: 'transparent',
  },
  filenameBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    padding: 6,
  },
  photoName: {
    color: '#fff',
    fontSize: 11,
    textAlign: 'center',
  },
  uploadedBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: 12,
    padding: 2,
  },
  photoDeleting: {
    opacity: 0.6,
  },
  imageDeleting: {
    opacity: 0.5,
  },
  deletingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,0,0,0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  deletingText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  viewerModal: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    margin: 0,
    padding: 0,
    width: '100%',
    height: '100%',
  },
  viewerContainer: {
    flex: 1,
    flexDirection: 'column',
    position: 'relative',
    overflow: 'auto',
  },
  imageArea: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    flex: 1,
  },
  viewerImage: {
    maxWidth: '90%',
    maxHeight: '65vh',
    objectFit: 'contain',
  },
  viewerCloseButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  controlsArea: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: 20,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  photoInfo: {
    marginBottom: 12,
  },
  photoInfoText: {
    color: '#666',
    fontSize: 13,
    textAlign: 'center',
  },
  viewerNavLeft: {
    position: 'absolute',
    left: 20,
    top: '50%',
    marginTop: -24,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  viewerNavRight: {
    position: 'absolute',
    right: 20,
    top: '50%',
    marginTop: -24,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  noteBadge: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    backgroundColor: 'rgba(33, 150, 243, 0.9)',
    borderRadius: 12,
    padding: 2,
  },
  notePanel: {
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  noteViewContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
  },
  noteIcon: {
    marginRight: 8,
  },
  noteText: {
    flex: 1,
    color: '#333',
    fontSize: 14,
    lineHeight: 20,
  },
  noteEditButton: {
    margin: 0,
  },
  noteEditContainer: {
    gap: 12,
  },
  noteInput: {
    width: '100%',
    minHeight: 60,
    maxHeight: 100,
    padding: 12,
    backgroundColor: '#fff',
    color: '#333',
    border: '1px solid #ccc',
    borderRadius: 4,
    fontSize: 14,
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  noteActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 8,
  },
  downloadButtonContainer: {
    alignItems: 'center',
  },
};
